import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import useDebounce from "../../../Component/Hooks/useDebounce";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
} from "@heroicons/react/24/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlane, faTrain } from "@fortawesome/free-solid-svg-icons";

const MAX_ADDRESS_SUGGESTIONS = 5;

const AddressInput = ({
  label,
  color,
  fieldError,
  setFieldValue,
  initialValue = "",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Le composant est démonté pendant l'affichage du résultat : sans valeur
  // initiale, « Modifier la simulation » rouvrait le formulaire avec des
  // champs vides et `required` bloquait l'envoi.
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const debouncedValue = useDebounce(searchQuery, 300);
  const inputRef = useRef(null);
  const popupRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!debouncedValue || debouncedValue.length < 3) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();
    setIsSearching(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/widgets/taxi/geocode`, {
        params: { query: debouncedValue },
        signal: controller.signal,
      })
      .then((response) => {
        setSuggestions(response.data);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setSuggestions([]);
        }
      })
      .finally(() => {
        setIsSearching(false);
      });

    return () => controller.abort();
  }, [debouncedValue]);

  const handleSuggestionClick = (suggestion) => {
    setFieldValue(suggestion.name, suggestion.latitude, suggestion.longitude);
    setSearchQuery(suggestion.name);
    setIsPopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isPopupOpen &&
        overlayRef.current &&
        overlayRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopupOpen]);

  // Le type vient du géocodeur : classer sur le libellé rangerait « Avenue de
  // l'Aéroport » parmi les aéroports.
  const filteredSuggestions = {
    gares: suggestions.filter((s) => s.type === "station"),
    aeroports: suggestions.filter((s) => s.type === "airport"),
    villes: suggestions.filter((s) => !s.type),
  };

  const categoryLabels = {
    gares: "Gares",
    aeroports: "Aéroports",
    villes: "Adresses",
  };

  return (
    <div className="relative" ref={inputRef}>
      <label
        htmlFor={label}
        className="flex items-center gap-1.5 text-sm font-medium mb-1.5"
      >
        <MapPinIcon className="w-4 h-4 opacity-70" />
        {label}
      </label>
      <input
        type="text"
        id={label}
        name={label}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => setIsPopupOpen(true)}
        className="widget-input w-full border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 text-sm transition-shadow bg-white"
        required
        readOnly={isPopupOpen}
        placeholder={
          label.includes("départ")
            ? "Ex: 10 rue de la Paix, Paris"
            : "Ex: Aéroport Nice Côte d'Azur"
        }
      />

      {fieldError && (
        <span className="text-red-400 text-xs mt-1 block">
          Impossible de trouver l'adresse.
        </span>
      )}

      {/* Overlay */}
      {isPopupOpen && (
        <div
          ref={overlayRef}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-10"
        />
      )}

      {/* Address search popup */}
      {isPopupOpen && (
        <div
          ref={popupRef}
          className="fixed top-[10%] sm:top-1/4 left-1/2 -translate-x-1/2 w-[92%] sm:w-[80%] max-w-lg bg-white rounded-2xl shadow-2xl z-20 overflow-hidden"
        >
          {/* Popup header with search */}
          <div className="flex items-center gap-2 p-3 sm:p-4 border-b border-gray-100">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="Rechercher une adresse..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border-none focus:outline-none text-gray-800 text-sm placeholder-gray-400"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setIsPopupOpen(false)}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
            >
              <XMarkIcon className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Suggestions list */}
          <div className="max-h-[50vh] overflow-y-auto styled-scrollbar p-2">
            {Object.keys(filteredSuggestions).map((category) => (
              <div key={category}>
                {filteredSuggestions[category].length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider px-2 py-1.5">
                      {categoryLabels[category]}
                    </p>
                    <ul>
                      {filteredSuggestions[category]
                        .slice(0, MAX_ADDRESS_SUGGESTIONS)
                        .map((suggestion, index) => (
                          <li
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="flex items-center gap-2.5 px-3 py-3 sm:py-2.5 cursor-pointer hover:bg-gray-50 active:bg-gray-100 rounded-xl text-gray-700 text-sm transition-colors min-h-[44px]"
                          >
                            {suggestion.type === "airport" ? (
                              <FontAwesomeIcon icon={faPlane} className="w-4 h-4 text-gray-400 shrink-0" />
                            ) : suggestion.type === "station" ? (
                              <FontAwesomeIcon icon={faTrain} className="w-4 h-4 text-gray-400 shrink-0" />
                            ) : (
                              <MapPinIcon className="w-4 h-4 text-gray-300 shrink-0" />
                            )}
                            <span className="truncate">{suggestion.name}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}

            {suggestions.length === 0 && !isSearching && searchQuery.length >= 3 && (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <MagnifyingGlassIcon className="w-8 h-8 opacity-30 mb-2" />
                <p className="text-sm">Aucun résultat trouvé</p>
              </div>
            )}

            {suggestions.length === 0 && !isSearching && searchQuery.length < 3 && (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <MapPinIcon className="w-8 h-8 opacity-30 mb-2" />
                <p className="text-sm">Tapez au moins 3 caractères</p>
              </div>
            )}

            {isSearching && (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                <p className="text-sm">Recherche...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressInput;
